import type { AutoTaskProgressRow, DevelopmentStatus, TaskRecord, TicketSpec } from "../types";
import type { Tone } from "./loopGlance";

/** A button the ticket's page offers for what it waits on. */
export interface TicketAction {
	kind: "discuss" | "confirm" | "deploy" | "loop" | "stop" | "resume";
	label: string;
}

/** Where a development ticket stands and the buttons for what it waits on. */
export interface TicketNext {
	tone: Tone;
	/** 下一步 when it waits for the person, 现在 otherwise. */
	label: "下一步" | "现在";
	text: string;
	/** The server's own words on why, when it gave some. */
	detail: string | null;
	actions: TicketAction[];
}

export interface TicketNextInput {
	task: Pick<TaskRecord, "archived"> & { status: DevelopmentStatus };
	/** Undefined while it loads. */
	spec: TicketSpec | null | undefined;
	progress: AutoTaskProgressRow | null;
	/** Whether auto mode picks up; null where the server has none. */
	auto: boolean | null;
}

const DISCUSS: TicketAction = { kind: "discuss", label: "继续 dev 讨论" };
const RETURN: TicketAction = { kind: "discuss", label: "交回 dev" };
const DEPLOY: TicketAction = { kind: "deploy", label: "部署 ›" };
const LOOP: TicketAction = { kind: "loop", label: "打开 Loop" };

const short = (sha: string) => sha.slice(0, 8);

/** What a development ticket waits on (ADR-0073, CONTEXT.md "Auto progress"); null while its spec loads. */
export function ticketNext({ task, spec, progress, auto }: TicketNextInput): TicketNext | null {
	const say = (tone: Tone, text: string, actions: TicketAction[] = [], detail: string | null = null): TicketNext => ({
		tone,
		label: tone === "act" || (tone === "warn" && actions.length > 0) || tone === "err" ? "下一步" : "现在",
		text,
		detail,
		actions,
	});
	const loop = progress?.loopId ? [LOOP] : [];

	if (task.archived) return say("dim", "已归档");
	if (task.status === "completed") return say("ok", "已完成", loop);

	if (task.status === "ready_to_deploy") {
		const delivery = progress?.delivery;
		if (delivery?.state === "deploy_failed") return say("err", "部署失败", [DEPLOY, ...loop], delivery.reason);
		const sha = delivery?.pushedSha ? `（${short(delivery.pushedSha)}）` : "";
		return say("act", `已推送到 main${sha} · 待部署`, [DEPLOY, ...loop]);
	}

	if (task.status === "in_progress") return running(progress, say, loop);

	if (spec === undefined) return null;
	if (spec === null) return say("act", "需求未明确", [{ kind: "discuss", label: "在 dev 讨论中明确需求" }]);
	if (spec.stale) return say("warn", "spec 已过期", [DISCUSS]);
	if (!spec.confirmed) return say("act", "spec 待确认", [{ kind: "confirm", label: "确认此版本" }, DISCUSS]);
	if (task.status === "undefined") return say("dim", "spec 已确认 · 待 dev 验证", [DISCUSS]);

	if (progress?.state === "skipped" && progress.reason) return say("warn", "已就绪 · 未接手", [], progress.reason);
	if (auto === false) return say("dim", "已就绪 · 自动模式未开启");
	return say("dim", "已就绪 · 待自动模式接手");
}

/** A picked ticket: its run, or where its passed change stands in the push lane. */
function running(progress: AutoTaskProgressRow | null, say: (tone: Tone, text: string, actions?: TicketAction[], detail?: string | null) => TicketNext, loop: TicketAction[]): TicketNext {
	if (!progress) return say("run", "进行中");
	const stop: TicketAction[] = progress.loopId ? [LOOP, { kind: "stop", label: "停止执行" }] : [];
	switch (progress.state) {
		case "running":
		case "started":
		case "launching":
			return say("run", "自动编码中", stop, progress.reason);
		case "awaiting_review":
			return say("act", "Loop 待确认", stop, progress.reason);
		case "waiting_quota":
			return say("run", "Codex 额度已用尽", stop, progress.reason);
		case "interrupted":
			return say("warn", "执行已中断", [{ kind: "resume", label: "恢复执行" }, ...loop], progress.reason);
		case "failed":
			return say("err", "编码失败", [RETURN, ...loop], progress.reason);
		case "launch_failed":
			return say("err", "启动失败", [RETURN, ...loop], progress.reason);
		case "aborted":
			return say("err", "已停止", [RETURN, ...loop], progress.reason);
	}
	const delivery = progress.delivery;
	if (delivery?.state === "queued") return say("run", "检查已通过 · 待推送", loop);
	if (delivery?.state === "integrating") return say("run", "合入检查中", loop);
	if (delivery?.state === "paused") return say("warn", "推送已暂停", [RETURN, ...loop], delivery.pause ?? delivery.reason);
	if (delivery?.state === "withdrawn") return say("warn", "已撤出推送", [RETURN, ...loop], delivery.reason);
	if (progress.state === "passed") return say("run", "检查已通过 · 待推送", loop);
	return say("run", "进行中", loop, progress.reason);
}
